import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../services';
import ApiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Handle token expiration
  const handleTokenExpired = () => {
    console.warn('Token expired, logging out user');

    // Show user notification only if user is currently authenticated
    if (isAuthenticated) {
      alert('Your session has expired. Please log in again.');
    }

    setUser(null);
    setIsAuthenticated(false);
    AuthService.logout();

    // Redirect to login page only if not already on a public page
    if (
      window.location.pathname.includes('/dashboard') ||
      window.location.pathname.includes('/admin')
    ) {
      window.location.href = '/complaint/login';
    }
  };

  useEffect(() => {
    // Set up token expiration callback
    ApiService.setTokenExpiredCallback(handleTokenExpired);

    const checkAuthStatus = async () => {
      try {
        const currentUser = AuthService.getCurrentUser();
        if (currentUser && AuthService.isAuthenticated()) {
          // If we have user data and token, assume user is authenticated
          // Token verification will happen on actual API calls
          setUser(currentUser);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth status check failed:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();

    // Listen for storage changes (logout in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        if (e.newValue === null) {
          // User logged out in another tab
          setUser(null);
          setIsAuthenticated(false);
        } else {
          // User logged in in another tab
          const newUser = JSON.parse(e.newValue);
          setUser(newUser);
          setIsAuthenticated(true);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const result = await AuthService.login(email, password);
     


      if (result.success) {
        setUser(result.user);
        setIsAuthenticated(true);
        return { success: true, user: result.user };
      } else {
        return { success: false, message: result.message };
      }
    } catch (error) {
      return { success: false, message: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      AuthService.logout();
      setUser(null);
      setIsAuthenticated(false);

      // Clear any cached data
      if (
        window.location.pathname.includes('/admin') ||
        window.location.pathname.includes('/dashboard')
      ) {
        window.location.href = '/complaint/login';
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const hasRole = (role) => {
    return user && user.role === role;
  };

  const hasAnyRole = (roles) => {
    return user && roles.includes(user.role);
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    hasRole,
    hasAnyRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
